/**
 * Role-based navigation routes configuration
 * This file defines which routes are visible for different user roles
 */

// Import all routes
import routes from "../routes";

// Import React and Material UI components
import React from "react";
import Icon from "@mui/material/Icon";

// Import specific components for role-based routes

import TerimaanHasil from "../page/KEW/TerimaanHasil";
import TerimaanLaporan from "../page/KEW/TerimaanLaporan";
import NotisTunggakan from "../page/UUU/NotisTunggakan";
import AdminHome from "../page/Admin/AdminHome";
import NamaList from "../page/Admin/NamaList";
import AkaunList from "../page/Admin/AkaunList";
import HartaList from "../page/Admin/HartaList";
import KategoriKutipanList from "../page/Admin/KategoriKutipanList";
import ZonList from "../page/Admin/ZonList";
import InboisList from "../page/Admin/InboisList";
import LejarList from "../page/Admin/LejarList";
import TerimaanList from "../page/Admin/TerimaanList";
import DepositList from "../page/Admin/DepositList";
import AdminSession from "../page/Admin/Session";

/**
 * Role-based route filtering configuration
 * Routes are organized by menu type (from pengguna.menu column)
 * 
 * Menu Types:
 * - ADMIN: Full admin access to all tables and settings
 * - KAUNTER: Counter staff (limited to Terimaan and Laporan)
 * - BAHAGIAN: Department staff (full access to their department's data)
 */
const roleBasedRoutes = {
  /**
   * KAUNTER (Counter Staff)
   * Limited access: Dashboard, Terimaan and Laporan sections
   * Example: user 'huda' with menu='KAUNTER'
   */
  KAUNTER: [
    {
      type: "title",
      title: "Dashboard",
      key: "dashboard-title",
    },
    {
      type: "collapse",
      name: "Dashboard",
      key: "dashboard",
      route: "/dashboard",
      component: routes.find(r => r.key === "dashboard")?.component,
      icon: <Icon fontSize="small">dashboard</Icon>,
    },
    {
      type: "title",
      title: "Terimaan",
      key: "terimaan-title",
    },
    {
      type: "collapse",
      name: "Terimaan Hasil",
      key: "terimaan-hasil",
      route: "/terimaan/terimaan-hasil",
      component: <TerimaanHasil />,
      icon: <Icon fontSize="small">receipt_long</Icon>,
    },
    {
      type: "title",
      title: "Laporan",
      key: "laporan-title",
    },
    {
      type: "collapse",
      name: "Laporan Terimaan Hasil",
      key: "laporan-terimaan",
      route: "/laporan/terimaan",
      component: <TerimaanLaporan />,
      icon: <Icon fontSize="small">assessment</Icon>,
    },
  ],

  /**
   * BAHAGIAN (Department Staff)
   * Full access to all features for their department
   * This is the default for most users (UEU, HT, PBD, etc.)
   */
  BAHAGIAN: routes,

  /**
   * ADMIN (System Administrators)
   * Full access to admin panel and all system tables
   */
  ADMIN: [
    {
      type: "title",
      title: "Admin",
      key: "admin-title",
    },
    {
      type: "collapse",
      name: "Admin",
      key: "admin-home",
      route: "/admin",
      component: <AdminHome />,
      icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    },
  { type: "collapse", name: "Nama", key: "admin-nama", route: "/admin/nama", component: <NamaList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Akaun", key: "admin-akaun", route: "/admin/akaun", component: <AkaunList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Harta", key: "admin-harta", route: "/admin/harta", component: <HartaList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Kategori Kutipan", key: "admin-kategori-kutipan", route: "/admin/kategori-kutipan", component: <KategoriKutipanList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Zon", key: "admin-zon", route: "/admin/zon", component: <ZonList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Inbois", key: "admin-inbois", route: "/admin/inbois", component: <InboisList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Lejar", key: "admin-lejar", route: "/admin/lejar", component: <LejarList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Terimaan", key: "admin-terimaan", route: "/admin/terimaan", component: <TerimaanList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Deposit", key: "admin-deposit", route: "/admin/deposit", component: <DepositList />, icon: <Icon fontSize="small">list</Icon> },
  { type: "collapse", name: "Session", key: "admin-session", route: "/admin/sessions", component: <AdminSession />, icon: <Icon fontSize="small">security</Icon> },
  ],
};

/**
 * Get routes based on user information
 * Routes are determined by menu type (pengguna.menu column)
 * 
 * @param {Object} user - User object with id_pengguna, bahagian, menu, etc.
 * @returns {Array} - Array of route objects for the user
 */
export const getRoutesForUser = (user) => {
  if (!user) {
    return roleBasedRoutes.BAHAGIAN;
  }

  // Get menu type from user object (normalized to uppercase)
  const menu = (user.menu || "").trim().toUpperCase();
  const userId = (user.id_pengguna || "").toLowerCase();
  
  // Admin users get admin routes
  if (menu === "ADMIN" || userId === "admin") {
    return roleBasedRoutes.ADMIN;
  }

  // Counter staff (KAUNTER) get limited access
  if (menu === "KAUNTER") {
    return roleBasedRoutes.KAUNTER;
  }

  // Department staff (BAHAGIAN) and any other menu types get full department access
  if (menu === "BAHAGIAN" || roleBasedRoutes[menu]) {
    return roleBasedRoutes[menu] || roleBasedRoutes.BAHAGIAN;
  }

  // Default: return BAHAGIAN routes (full department access)
  console.warn(`Unknown menu type '${menu}' for user '${userId}', defaulting to BAHAGIAN routes`);
  return roleBasedRoutes.BAHAGIAN;
};

/**
 * Check if user has access to a specific route
 * @param {Object} user - User object
 * @param {string} routePath - Route path to check
 * @returns {boolean} - True if user has access
 */
export const hasAccessToRoute = (user, routePath) => {
  const userRoutes = getRoutesForUser(user);
  return userRoutes.some((route) => route.route === routePath);
};

export default roleBasedRoutes;
